import ArticleIcon from "@mui/icons-material/Article";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Divider, IconButton, Modal, Typography } from "@mui/material";

import InfoSection from "./InfoSection";
import StatCard from "./StatCard";
import ScoreIcon from "../../../icons/ScoreIcon";

// Display interface for modal (mapped from API data)
interface SocialInsightDisplay {
  category: string;
  views: number;
  posts: number;
  dataInsight: string;
  audienceTrend: string;
  action: string;
  relevanceScore: string;
  relevanceAssessment: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  categoryData: SocialInsightDisplay | null;
}

const CategoryModal = ({ open, onClose, categoryData }: Props) => {
  if (!categoryData) return null;

  const formatNumber = (num: number) => {
    if (num >= 1000000000) {
      const value = (num / 1000000000).toFixed(1);
      return value.endsWith(".0") ? value.slice(0, -2) + "B" : value + "B";
    }
    if (num >= 1000000) {
      const value = (num / 1000000).toFixed(1);
      return value.endsWith(".0") ? value.slice(0, -2) + "M" : value + "M";
    }
    if (num >= 1000) {
      const value = (num / 1000).toFixed(1);
      return value.endsWith(".0") ? value.slice(0, -2) + "K" : value + "K";
    }
    return num.toString();
  };



  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="category-modal-title"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: "90%", md: "70%" },
          height: { xs: "90%", sm: "90%", md: "90%" },
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: "16px",
          boxShadow: 24,
          p: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 3,
            borderBottom: "1px solid #e0e0e0",
            background: "#E77825"
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography fontWeight="600" fontSize="24px" color="white">
              {categoryData.category}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "text.secondary" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 4, overflowY: "auto", flex: 1, bgcolor: "#fafbfc" }}>
          {/* Stats */}
          <Box sx={{ display: "flex", gap: 2.5, mb: 4 }}>
            <StatCard
              icon={<VisibilityIcon sx={{ fontSize: 36, color: "#E87722" }} />}
              value={formatNumber(categoryData.views)}
              label="Total Views"
            />

            <StatCard
              icon={<ArticleIcon sx={{ fontSize: 36, color: "#E87722" }} />}
              value={formatNumber(categoryData.posts)}
              label="Total Posts"
            />

            <StatCard
              icon={<ScoreIcon />}
              value={categoryData.relevanceScore}
              label="Relevance Score"
            />
          </Box>

          <Divider sx={{ my: 3.5, borderColor: "#e5e7eb" }} />

          <InfoSection
            title="Data Insight"
            content={categoryData.dataInsight}
            sx={{ mb: 3.5 }}
          />

          <InfoSection
            title="Audience Trend"
            content={categoryData.audienceTrend}
            sx={{ mb: 3.5 }}
          />

          <InfoSection
            title="Recommended Action"
            content={categoryData.action}
            sx={{ mb: 3.5 }}
          />

          <InfoSection
            title="Relevance Assessment"
            content={categoryData.relevanceAssessment}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default CategoryModal;
